<!--
  Public KYC submission page reached via the magic link sent to the
  supplier. No portal login — the URL token is verified on load.

  Premium layout:
    • Full-width hero with deep-teal gradient + subtle pattern overlay
    • 3-step progress indicator (Upload → Submit → Review)
    • Document cards stacked with layered chrome
    • Info notice card
    • Gradient primary CTA with hover micro-interaction
-->
<section class="ktp-page"
         role="main"
         [class.ktp-page--dragging]="isDraggingFile">

  <!-- Page-level drag overlay. Shown when a file is being dragged
       anywhere on the page so the supplier knows drop is supported. -->
  <div *ngIf="isDraggingFile"
       class="ktp-drop-overlay"
       aria-hidden="true">
    <div class="ktp-drop-overlay__inner">
      <mat-icon class="ktp-drop-overlay__icon">cloud_upload</mat-icon>
      <p class="ktp-drop-overlay__title">Drop on a document slot</p>
      <p class="ktp-drop-overlay__hint">
        Release the file over the matching document card below.
      </p>
    </div>
  </div>


  <!-- =================================================================
       HERO — full-bleed deep-teal banner.
       Three pieces on one horizontal axis at md+:
         · Logo chip at the left corner
         · Page title (KYC eyebrow + main line) centered between
         · Verified-supplier pill at the right corner
       Stacks gracefully on small screens.
       ================================================================= -->
  <header class="ktp-hero">
    <div class="ktp-hero__pattern" aria-hidden="true"></div>
    <div class="ktp-hero__inner">

      <div class="ktp-hero__row">

        <!-- LEFT — Brand logo chip -->
        <div class="ktp-hero__brand">
          <img class="ktp-hero__logo"
               src="https://mps-cdn-dev.s3.eu-west-1.amazonaws.com/assets/images/ihm-app-logo.png"
               alt="Varuna Sentinels"/>
        </div>

        <!-- CENTER — Page title -->
        <h1 class="ktp-hero__title">
          <span class="ktp-hero__title-eyebrow">KYC</span>
          <span class="ktp-hero__title-main">
            Supplier Verification &middot; Document Upload Portal
          </span>
        </h1>

        <!-- RIGHT — Verified supplier pill -->
        <div class="ktp-hero__userwrap"
             *ngIf="pageState === 'LOADED' ||
                    pageState === 'SUBMITTING' ||
                    pageState === 'SUBMITTED'; else heroUserSpacer">
          <div class="ktp-hero__user">
            <span class="ktp-hero__avatar" aria-hidden="true">
              {{ (supplierFirstName.charAt(0) || 'S') | uppercase }}
            </span>
            <div class="ktp-hero__user-meta">
              <!-- 2026-10-26 (G1 review L6): "Verified" only once this KYC request was ACCEPTED; a supplier still
                   submitting is not verified yet. -->
              <span class="ktp-hero__user-eyebrow">{{ kycRequestAccepted ? 'Verified Supplier' : 'Supplier' }}</span>
              <span class="ktp-hero__user-name" [title]="supplierFirstName + ' ' + supplierLastName">
                {{ supplierFirstName }} {{ supplierLastName }}
              </span>
            </div>
            <mat-icon class="ktp-hero__user-check" aria-hidden="true" *ngIf="kycRequestAccepted">verified</mat-icon>
          </div>
        </div>

        <!-- Spacer reserves the right-column width while the page is
             still VERIFYING / ERROR so the centred title doesn't drift
             leftward as the user pill appears post-verify. -->
        <ng-template #heroUserSpacer>
          <div class="ktp-hero__userwrap ktp-hero__userwrap--placeholder"
               aria-hidden="true"></div>
        </ng-template>

      </div>

    </div>
  </header>

  <!-- =================================================================
       BODY — contained shell that hosts every page state below the hero
       ================================================================= -->
  <div class="ktp-shell">

    <!-- ===================== 1. VERIFYING ============================ -->
    <div *ngIf="pageState === 'VERIFYING'" class="ktp-loading">
      <div class="ktp-loading__halo" aria-hidden="true"></div>
      <mat-progress-spinner mode="indeterminate" diameter="56"></mat-progress-spinner>
      <p class="ktp-loading__primary">Verifying your secure link</p>
      <p class="ktp-loading__secondary">
        This usually takes just a moment.
      </p>
    </div>

    <!-- ===================== 2. ERROR ================================ -->
    <div *ngIf="pageState === 'ERROR'"
         class="ktp-card ktp-error-card"
         role="alert">
      <span class="ktp-card__icon-wrap ktp-card__icon-wrap--bad" aria-hidden="true">
        <mat-icon>error_outline</mat-icon>
      </span>
      <h2 class="ktp-card__title">{{ errorTitle() }}</h2>
      <p class="ktp-card__body">{{ errorBody() }}</p>
      <p class="ktp-card__support">
        Need help? Write to
        <a href="mailto:support@varuna-sentinels.com">
          support&#64;varuna-sentinels.com
        </a>.
      </p>
    </div>

    <!-- ===================== 3. LOADED + SUBMITTING ================== -->
    <ng-container
      *ngIf="pageState === 'LOADED' || pageState === 'SUBMITTING'">

      <!-- 3-step progress indicator -->
      <nav class="ktp-steps" aria-label="Submission progress">
        <ol class="ktp-steps__list">

          <li class="ktp-steps__item ktp-steps__item--active">
            <span class="ktp-steps__dot">
              <mat-icon aria-hidden="true">cloud_upload</mat-icon>
            </span>
            <span class="ktp-steps__label">Upload</span>
          </li>

          <li class="ktp-steps__line ktp-steps__line--active"
              aria-hidden="true"></li>

          <li class="ktp-steps__item"
              [class.ktp-steps__item--active]="pageState === 'SUBMITTING'">
            <span class="ktp-steps__dot">
              <mat-icon aria-hidden="true">task_alt</mat-icon>
            </span>
            <span class="ktp-steps__label">Submit</span>
          </li>

          <li class="ktp-steps__line"
              [class.ktp-steps__line--active]="pageState === 'SUBMITTING'"
              aria-hidden="true"></li>

          <li class="ktp-steps__item">
            <span class="ktp-steps__dot">
              <mat-icon aria-hidden="true">how_to_reg</mat-icon>
            </span>
            <span class="ktp-steps__label">Review</span>
          </li>

        </ol>
      </nav>

      <!-- Empty-bundle guard -->
      <div *ngIf="slots.length === 0" class="ktp-card ktp-empty-card">
        <span class="ktp-card__icon-wrap" aria-hidden="true">
          <mat-icon>folder_open</mat-icon>
        </span>
        <h2 class="ktp-card__title">No documents requested</h2>
        <p class="ktp-card__body">
          This invitation does not list any documents to upload. Please
          contact the buyer if you believe this is a mistake.
        </p>
      </div>

      <!-- Main form -->
      <form *ngIf="slots.length > 0"
            class="ktp-form"
            (ngSubmit)="onSubmit()"
            novalidate>

        <!-- Greeting strip inside the form (above the slots) -->
        <div class="ktp-greeting-card">
          <div class="ktp-greeting-card__main">
            <span class="ktp-greeting-card__eyebrow">Welcome</span>
            <h2 class="ktp-greeting-card__title">
              Hi {{ supplierFirstName }},
              <span class="ktp-greeting-card__title-soft">
                let's get you verified
              </span>
            </h2>
            <p class="ktp-greeting-card__body">
              <strong>{{ buyerCompanyName }}</strong> has requested the
              documents below to complete your KYC verification.
            </p>
          </div>
          <div class="ktp-greeting-card__stat"
               [class.ktp-greeting-card__stat--full]="displayedFilledCount === slots.length && slots.length > 0">
            <span class="ktp-greeting-card__stat-count">
              {{ displayedFilledCount }}<span class="ktp-greeting-card__stat-total">/{{ slots.length }}</span>
            </span>
            <span class="ktp-greeting-card__stat-label">Documents ready</span>
          </div>
        </div>

        <!-- RFQ context strip — shown only when the KYC bundle is
             linked to an RFQ (Flow 2). Surfaces the RFQ ref and vessel
             so the supplier knows which RFQ this verification belongs
             to before they upload. -->
        <div *ngIf="rfqId" class="ktp-rfq-strip" aria-label="RFQ context">
          <span class="ktp-rfq-strip__chip">
            <mat-icon class="ktp-rfq-strip__icon" aria-hidden="true">tag</mat-icon>
            <span class="ktp-rfq-strip__label">RFQ Reference</span>
            <span class="ktp-rfq-strip__value">
              {{ rfqRefNumber || ('#' + rfqId) }}
            </span>
          </span>
          <span *ngIf="vesselName" class="ktp-rfq-strip__chip ktp-rfq-strip__chip--soft">
            <mat-icon class="ktp-rfq-strip__icon" aria-hidden="true">directions_boat</mat-icon>
            <span class="ktp-rfq-strip__label">Vessel</span>
            <span class="ktp-rfq-strip__value">{{ vesselName }}</span>
          </span>
        </div>

        <!-- Optional buyer note -->
        <div *ngIf="requestMessage" class="ktp-note">
          <mat-icon class="ktp-note__icon" aria-hidden="true">format_quote</mat-icon>
          <div class="ktp-note__body">
            <span class="ktp-note__label">Note from the buyer</span>
            <p class="ktp-note__text">{{ requestMessage }}</p>
          </div>
        </div>

        <!-- Document cards -->
        <ul class="ktp-slot-list">
          <li *ngFor="let slot of slots; let i = index; trackBy: trackBySlot"
              class="ktp-slot-wrap"
              [style.animation-delay.ms]="i * 80"
              (mousemove)="onSlotMouseMove($event)">
            <span class="ktp-slot-wrap__spotlight" aria-hidden="true"></span>
            <div class="ktp-slot-wrap__rail">
              <!-- Editorial numeral — large outlined display number that
                   gives each slot a distinct identity at a glance. -->
              <span class="ktp-slot-wrap__numeral" aria-hidden="true">
                {{ (i + 1) | number:'2.0-0' }}
              </span>
              <div class="ktp-slot-wrap__rail-meta">
                <span class="ktp-slot-wrap__index">Document {{ i + 1 }}</span>
                <span class="ktp-slot-wrap__name" [title]="slot.doc.documentName">
                  {{ slot.doc.documentName }}
                </span>
              </div>
            </div>
            <div class="ktp-slot-wrap__body">
              <app-kyc-token-slot
                [slot]="slot"
                [disabled]="pageState === 'SUBMITTING'"
                (filePicked)="onSlotPick(slot, $event)"
                (fileRemoved)="onSlotRemove(slot)"
                (fileReplaceRequested)="onSlotReplaceRequested(slot)"
                (filePreviewRequested)="onSlotPreviewRequested(slot)"
                (retryRequested)="onSlotRetryRequested(slot)">
              </app-kyc-token-slot>
            </div>
          </li>
        </ul>

        <!-- Info notice -->
        <aside class="ktp-info" aria-label="Submission guidelines">
          <mat-icon class="ktp-info__icon" aria-hidden="true">info_outline</mat-icon>
          <div class="ktp-info__body">
            <p class="ktp-info__lead">
              Please ensure all required documents are clear and in valid
              formats (PDF, JPG or PNG, up to 10&nbsp;MB each).
            </p>
            <ul class="ktp-info__list">
              <li>
                <strong>Privacy</strong> — files are sent directly to our
                secure storage and shared only with
                <strong>{{ buyerCompanyName }}</strong>.
              </li>
              <li>
                <strong>Link validity</strong> — this invitation link will
                expire in 30 days from its issue date.
              </li>
              <li>
                <strong>Need a change later?</strong> — you can reopen this
                link any time before it expires to replace a file.
              </li>
            </ul>
          </div>
        </aside>

        <!-- Footer with Submit CTA -->
        <footer class="ktp-footer">
          <p class="ktp-footer__hint">
            By submitting you confirm the uploaded files are accurate and
            ready for verification.
          </p>
          <button mat-flat-button
                  type="submit"
                  class="ktp-submit-btn"
                  [disabled]="!canSubmit() || pageState === 'SUBMITTING'"
                  [attr.aria-busy]="pageState === 'SUBMITTING'">
            <span class="ktp-submit-btn__shine" aria-hidden="true"></span>
            <mat-progress-spinner *ngIf="pageState === 'SUBMITTING'"
                                  mode="indeterminate"
                                  diameter="22">
            </mat-progress-spinner>
            <mat-icon *ngIf="pageState !== 'SUBMITTING'" aria-hidden="true">
              task_alt
            </mat-icon>
            <span *ngIf="pageState !== 'SUBMITTING'">Submit for Verification</span>
            <span *ngIf="pageState === 'SUBMITTING'">Finalising your submission…</span>
          </button>
        </footer>

      </form>

    </ng-container>

    <!-- ===================== 4. SUBMITTED ============================ -->
    <div *ngIf="pageState === 'SUBMITTED'" class="ktp-card ktp-success-card">
      <!-- Confetti burst — 12 CSS-animated particles that fly out from
           behind the check icon for ~1.4 s, then disappear. Pure CSS,
           no JS scheduler. -->
      <div class="ktp-confetti" aria-hidden="true">
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
        <span class="ktp-confetti__piece"></span>
      </div>
      <span class="ktp-card__icon-wrap ktp-card__icon-wrap--ok" aria-hidden="true">
        <span class="ktp-card__icon-pulse"></span>
        <mat-icon>check_circle</mat-icon>
      </span>
      <h2 class="ktp-card__title" tabindex="-1">
        Your KYC documents have been submitted
      </h2>
      <p class="ktp-card__body">
        Thank you. The buyer will review your documents and reach out if
        anything else is needed.
      </p>
      <div class="ktp-success-card__meta">
        <span class="ktp-success-card__meta-dot" aria-hidden="true"></span>
        Reference: KYC&hairsp;-&hairsp;<span class="ktp-success-card__meta-ref">
          {{ supplierFirstName || 'submission' }}-confirmed
        </span>
      </div>
      <p class="ktp-card__support">You can safely close this tab.</p>
    </div>

  </div>

</section>
